import { sortBy } from '@helpers/Formatters';
import { setEqual } from '@helpers/Parsers';
import { getCurrentUser, getCurrentUserId } from '@helpers/firebase/Auth';
import db, { addDocWithTimestamps, parseFirestoreDoc } from '@helpers/firebase/Firestore';
import { firestore } from '@helpers/firebase/Initialize';
import { UserInfo } from '@types';
import { Chat, Message } from '@types';

const CHAT_COLLECTION = 'chats';
const MESSAGE_SUBCOLLECTION = 'messages';

type CurrentUser = ReturnType<typeof getCurrentUser>;

function parseChatDoc(doc: db.DocumentSnapshot) {
  return parseFirestoreDoc<Chat>(doc, 'chatId');
}

function parseMessageDoc(doc: db.DocumentSnapshot) {
  return parseFirestoreDoc<Message>(doc, 'messageId');
}

export async function addChat(userDocs: readonly UserInfo[]) {
  const currentUser = getCurrentUser();
  const chatUserDocs = getChatUsersWithCreatedByUser(userDocs, currentUser);
  const userIds = chatUserDocs.map((userDoc) => userDoc.userId);
  const data: Omit<Chat, 'chatId' | 'createdAt' | 'updatedAt'> = {
    createdBy: currentUser.uid,
    userIds,
    userDocs: chatUserDocs.map(({ userId, displayName, photoUrl }) => ({
      userId,
      displayName,
      photoUrl,
    })),
    unreadMessageCount: initializeUnreadMessageCount(userIds),
  };
  return await addDocWithTimestamps(db.collection(firestore, CHAT_COLLECTION), data);
}

export function getChatUsersWithCreatedByUser(
  userDocs: readonly UserInfo[],
  currentUser: CurrentUser
) {
  if (userDocs.some((user) => user.userId === currentUser.uid)) {
    return [...userDocs];
  }

  return [
    ...userDocs,
    {
      userId: currentUser.uid,
      displayName: currentUser.displayName,
      photoUrl: currentUser.photoURL ?? '',
    },
  ];
}

function initializeUnreadMessageCount(userIds: string[]) {
  const unreadMessageCount: Record<string, number> = {};
  for (const userId of userIds) {
    unreadMessageCount[userId] = 0;
  }
  return unreadMessageCount;
}

export async function getChat(chatId: string) {
  const docSnap = await db.getDoc(db.doc(firestore, CHAT_COLLECTION, chatId));
  return parseChatDoc(docSnap);
}

export async function addChatMessage(chatId: string, text: string) {
  const currentUser = getCurrentUser();
  const data: Omit<Message, 'messageId' | 'createdAt'> & { createdAt: db.FieldValue } = {
    text,
    createdAt: db.serverTimestamp(),
    user: {
      userId: currentUser.uid,
      // TODO: remove these and just lookup from chat doc
      displayName: currentUser.displayName,
      photoUrl: currentUser.photoURL ?? '',
    },
  };
  return await db.addDoc(
    db.collection(firestore, CHAT_COLLECTION, chatId, MESSAGE_SUBCOLLECTION),
    data
  );
}

export async function updateChatRead(chatId: string) {
  const currentUserId = getCurrentUserId();
  await db.updateDoc(db.doc(firestore, CHAT_COLLECTION, chatId), {
    [`unreadMessageCount.${currentUserId}`]: 0,
  });
}

export function checkForExistingChat(chats: readonly Chat[], userIds: readonly string[]) {
  const currentUserId = getCurrentUserId();
  const userIdsSet = new Set([...userIds, currentUserId]);
  for (const chat of chats) {
    const chatUserIdsSet = new Set(chat.userIds);
    if (setEqual(userIdsSet, chatUserIdsSet)) {
      return chat;
    }
  }
  return false;
}

export async function addOrOpenChat(chats: readonly Chat[], users: readonly UserInfo[]) {
  const existingChat = checkForExistingChat(
    chats,
    users.map((user) => user.userId)
  );
  if (existingChat) {
    return existingChat.chatId;
  }

  const chatRef = await addChat(users);
  return chatRef.id;
}

export async function getUserChats() {
  const currentUserId = getCurrentUserId();
  const q = db.query(
    db.collection(firestore, 'chats'),
    db.where('userIds', 'array-contains', currentUserId)
  );
  const querySnapshot = await db.getDocs(q);
  const chats = querySnapshot.docs.map((doc) => parseChatDoc(doc));
  return sortBy(chats, 'updatedAt', 'desc');
}

export function listenToMessages(
  chatId: string,
  onMessage: (messages: Message[]) => void,
  onError: (error: db.FirestoreError) => void
) {
  const onNext = (snapshot: db.QuerySnapshot<db.DocumentData>) => {
    const messages = snapshot.docs.map((doc) => parseMessageDoc(doc));
    onMessage(messages);
  };
  const q = db.query(
    db.collection(firestore, CHAT_COLLECTION, chatId, MESSAGE_SUBCOLLECTION),
    db.orderBy('createdAt', 'desc')
  );
  return db.onSnapshot(q, onNext, onError);
}

export function listenToChat(
  chatId: string,
  onChat: (chat: Chat) => void,
  onError: (error: db.FirestoreError) => void
) {
  return db.onSnapshot(
    db.doc(firestore, CHAT_COLLECTION, chatId),
    (doc) => onChat(parseChatDoc(doc)),
    onError
  );
}

export function listenToChats(
  onChats: (chats: Chat[]) => void,
  onError: (error: db.FirestoreError) => void,
  constraints: db.QueryConstraint[] = []
) {
  const currentUserId = getCurrentUserId();
  const q = db.query(
    db.collection(firestore, CHAT_COLLECTION),
    db.where('userIds', 'array-contains', currentUserId),
    ...constraints
  );
  const onNext = (snapshot: db.QuerySnapshot<db.DocumentData>) => {
    const chats = snapshot.docs.map((doc) => parseChatDoc(doc));
    onChats(chats);
  };
  return db.onSnapshot(q, onNext, onError);
}
